/**
 * How big the signer can make the document they are about to sign.
 *
 * Asked for by Mays: on the e-sign page the offer is rendered to fit
 * the pane, which on a phone puts a purchase and sale agreement at
 * about six-point type. People were signing something they could not
 * read.
 *
 * Zooming redraws the page at a larger scale rather than stretching
 * the picture already on screen — the point of looking closer is to
 * read the words, and an upscaled bitmap only makes them bigger and
 * blurrier.
 */

/** Fit, then five steps up. 1 is what everyone saw before. */
export const SIGN_ZOOM_STEPS = Object.freeze([1, 1.25, 1.5, 2, 2.5, 3]);

/** The pdf.js scale behind a fitted page. */
export const SIGN_BASE_SCALE = 1.25;

/**
 * The canvas bitmaps are what this costs. A letter page at scale 3.75
 * is roughly 2,300 x 2,970 — about 27 MB of canvas — and a phone
 * starts dropping canvases when one document holds too many at once.
 * A long exhibit gets a lower ceiling and leans on the browser to
 * stretch the last stop or two: softer than it could be, but never a
 * document that comes back blank.
 */
export function signRenderCeiling(pageCount) {
  if (pageCount > 12) return 2;
  if (pageCount > 6) return 2.75;
  return 3.75;
}

/** The pdf.js scale to draw at, for this zoom and this many pages. */
export function signRenderScale(zoom, pageCount) {
  const safeZoom = SIGN_ZOOM_STEPS.includes(zoom) ? zoom : 1;
  return Math.min(SIGN_BASE_SCALE * safeZoom, signRenderCeiling(pageCount));
}

/**
 * One step bigger or smaller. Returns the same zoom at either end, so
 * the caller never lands off the list.
 */
export function stepSignZoom(zoom, direction) {
  const at = SIGN_ZOOM_STEPS.indexOf(zoom);
  if (at < 0) return 1;
  const next = at + (direction > 0 ? 1 : -1);
  if (next < 0 || next >= SIGN_ZOOM_STEPS.length) return zoom;
  return SIGN_ZOOM_STEPS[next];
}
